import React from 'react';
import { FastForward, Zap, Palette, TrendingDown, Boxes, Paintbrush, SunMedium, Image as ImageIcon, ChevronRight } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { mockData } from '../mock';

const iconComponents = {FastForward, Palette, TrendingDown };

const normalSteps = [
  { n: 1, title: 'デザイン作成', thumb: '/images/step1.png' },
  { n: 2, title: 'プロトタイプ製造', thumb: '/images/step2.png' },
  { n: 3, title: 'プロトタイプ完成', thumb: '/images/step3.png' },
  { n: 4, title: 'スタジオ撮影', thumb: '/images/step4.png' },
  { n: 5, title: 'サムネイル完成', thumb: '/images/step5.png' },
];

const steps = [
  {
    title: 'モデリング',
    desc: '商品の形を作る作業。必要に応じて撮影用の背景セットも一緒に作成します。',
    thumb: '/images/1.png',
  },
  {
    title: 'テクスチャリング',
    desc: '表面の質感や色・ラベル画像を適用。つや・マットなど素材感も調整します。',
    thumb: '/images/2.png',
  },
  {
    title: 'ライティング',
    desc: '光の当て方を調整。強さ・角度・色温度まで最適化して読みやすさを向上。',
    thumb: '/images/3.png',
  },
  {
    title: 'レンダリング',
    thumb: '/images/4.png',
  },
];

const SolutionsSection = () => {
  return (
    <section id="service" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* タイトル */}
        <div className="text-center mb-16">
          <h2 className="text-4xl lg:text-6xl font-black text-gray-900 mb-6 font-din tracking-tight">
            その悩み、<span className="font-noto-heading font-black text-transparent bg-gradient-to-r from-blue-600 to-green-500 bg-clip-text py-3 px-1">3Dモックアップ</span>が<br />
            <span className="text-green-600 font-extrabold">すべて解決</span>します。
          </h2>
        </div>

        {/* 通常のサムネイル画像作成 - 背景付き */}
        <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-3xl p-6 sm:p-8 lg:p-10 border border-gray-200 mb-16">
          <h2 className="text-xl sm:text-2xl text-gray-700 font-bold mb-12">
            通常のサムネイル画像作成
          </h2>

          {/* タイムライン */}
          <div className="relative mb-12 flex items-center justify-between">
            {/* 矢印付きライン */}
            <div className="relative w-full h-2 bg-gray-500 rounded-full">
              {/* 左矢印 */}
              <div className="absolute -left-2 top-1/2 -translate-y-1/2">
                <div
                  className="w-0 h-1 border-t-[12px] border-b-[8px] border-r-[15px] border-transparent border-r-gray-500"
                />
              </div>

              {/* 右矢印 */}
              <div className="absolute -right-2 top-1/2 -translate-y-1/2">
                <div
                  className="w-0 h-0 border-t-[12px] border-b-[8px] border-l-[15px] border-transparent border-l-gray-500"
                />
              </div>

              {/* テキスト */}
              <div className="absolute inset-0 -translate-y-7 flex items-center justify-center">
                <span className="ttext-gray-600 font-black tracking-wide text-2xl sm:text-3xl lg:text-3xl">
                  1か月〜2か月
                </span>
              </div>
            </div>
          </div>

          {/* ステップ5つ */}
          <div className="grid gap-2 md:grid-cols-5">
            {normalSteps.map((s, i) => (
              <div key={s.n} className="relative">
                <div className="rounded-2xl bg-gray-50 ring-1 ring-gray-100 p-4 shadow-sm">
                  <div className="mb-4 overflow-hidden rounded-xl bg-white">
                    <img
                      src={s.thumb}
                      alt={s.title}
                      className="w-full aspect-[4/3] object-contain"
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                  <p className="text-center text-base font-semibold text-gray-900 whitespace-nowrap">
                    {s.n}. {s.title}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 3Dモックアップの説明（4工程） */}
        <div className="mb-16 rounded-2xl border border-gray-100 bg-gradient-to-br from-sky-50 to-emerald-50 p-6 lg:p-8">
          {/* 見出し行 */}
          <div className="flex flex-col gap-2 lg:flex-row lg:items-end lg:justify-between mb-8">
            <div>
              <p className="text-2xl lg:text-3xl font-extrabold text-sky-700 mb-1">
                3Dモックアップとは？
              </p>
              <p className="text-lg text-gray-700">
                3Dデータで作成した商品モデルを用いて、撮影なしで高品質な商品画像を用意する手法
              </p>
            </div>
          </div>

          {/* === 上段：矢印ライン === */}
          <div className="mt-10 relative mb-5 flex items-center justify-between">
            {/* 矢印付きライン */}
            <div className="relative w-full h-2 bg-sky-500 rounded-full">
              {/* 左矢印 */}
              <div className="absolute -left-2 top-1/2 -translate-y-1/2">
                <div
                  className="w-0 h-1 border-t-[12px] border-b-[8px] border-r-[15px] border-transparent border-r-sky-500"
                />
              </div>

              {/* 右矢印 */}
              <div className="absolute -right-2 top-1/2 -translate-y-1/2">
                <div
                  className="w-0 h-0 border-t-[12px] border-b-[8px] border-l-[15px] border-transparent border-l-sky-500"
                />
              </div>

              {/* テキスト */}
              <div className="mt-15 absolute inset-0 -translate-y-7 flex items-center justify-center">
                <span className="text-sky-700 font-black tracking-wide text-2xl sm:text-3xl lg:text-3xl">
                  1日～2週間
                </span>
              </div>
            </div>
          </div>

          {/* === 上段（1〜3工程） === */}
          <div className="grid gap-6 md:grid-cols-3 mb-10">
            {steps.slice(0, 3).map((s, i) => (
              <div key={s.title} className="relative">
                <div className="h-full rounded-2xl bg-white ring-1 ring-gray-100 p-5 shadow-sm hover:shadow-md transition-shadow">
                  <div className="mb-4 overflow-hidden rounded-2xl bg-gray-50 ring-1 ring-gray-100">
                    <img
                      src={s.thumb}
                      alt={s.title}
                      className="w-full h-[180px] md:h-[200px] object-cover"
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                  <h3 className="mb-2 text-lg font-semibold text-gray-900">
                    {i + 1}. {s.title}
                  </h3>
                  <p className="text-sm leading-relaxed text-gray-600">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>

          {/* === 下段（レンダリング） === */}
          <div className="flex flex-col items-center text-center">
            <div className="w-full md:w-2/3 lg:w-1/2 overflow-hidden rounded-2xl bg-gray-50 ring-1 ring-gray-100 mb-4">
              <img
                src={steps[3].thumb}
                alt={steps[3].title}
                className="w-full h-[300px] object-cover"
                loading="lazy"
                decoding="async"
              />
            </div>
            <div className="max-w-lg">
              <h3 className="mb-2 text-lg font-semibold text-gray-900">
                4. {steps[3].title}
              </h3>
              <p className="text-sm leading-relaxed text-gray-600">{steps[3].desc}</p>
            </div>
          </div>

          {/* フッターコピー */}
          <div className="text-center relative">
            {/* 背景のシャドウライン */}
            <div className="absolute left-1/2 top-0 -translate-x-1/2 w-[200px] h-3 rounded-full blur-md" />
            <p className="relative inline-block text-2xl md:text-3xl font-extrabold tracking-wide text-gray-900 leading-snug">
              <span className="text-teal-800">
                実物の撮影より早く、<span className="text-cyan-700 underline decoration-4 decoration-cyan-500 underline-offset-4">鮮明な商品画像</span>
              </span>
              を用意することが可能
            </p>
          </div>
        </div>


        {/* 既存：ベネフィット3カラム */}
        <div className="grid md:grid-cols-3 gap-8">
          {mockData.solutions.benefits.map((benefit, index) => {
            const IconComponent = iconComponents[benefit.icon];
            return (
              <Card
                key={index}
                className="bg-gradient-to-br from-blue-50 via-white to-green-50 border-0 shadow-xl hover:shadow-2xl transition-all duration-300 group transform hover:scale-110 hover:rotate-1"
              >
                <CardContent className="p-8 text-center relative overflow-hidden">
                  <div className="absolute -top-10 -right-10 w-32 h-32 bg-gradient-to-br from-blue-200/30 to-green-200/30 rounded-full"></div>
                  <div className="absolute -bottom-10 -left-10 w-24 h-24 bg-gradient-to-tr from-green-200/20 to-blue-200/20 rounded-full"></div>
                  
                  <div className="mb-6 flex justify-center relative z-10">
                    <div className="w-20 h-20 bg-gradient-to-r from-blue-600 to-green-500 rounded-2xl flex items-center justify-center group-hover:scale-110 group-hover:rotate-12 transition-all duration-300 shadow-2xl">
                      <IconComponent className="w-10 h-10 text-white" />
                    </div>
                  </div>
                  
                  <h3 className="text-2xl font-black text-gray-900 mb-4 font-din tracking-tight relative z-10">
                    {benefit.title}
                  </h3>
                  
                  <p className="text-gray-700 leading-relaxed font-body text-lg relative z-10">
                    {benefit.description}
                  </p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default SolutionsSection;


// import React from 'react';
// import { Zap, Palette, TrendingDown } from 'lucide-react';
// import { Card, CardContent } from './ui/card';
// import { mockData } from '../mock';

// const iconComponents = {
//   Zap,
//   Palette,
//   TrendingDown
// };

// const SolutionsSection = () => {
//   return (
//     <section id="service" className="py-20 bg-white">
//       <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
//         <div className="text-center mb-16">
//           <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
//             {mockData.solutions.title}
//           </h2>
//         </div>

//         <div className="grid md:grid-cols-3 gap-8">
//           {mockData.solutions.benefits.map((benefit, index) => {
//             const IconComponent = iconComponents[benefit.icon];
            
//             return (
//               <Card key={index} className="bg-gradient-to-br from-blue-50 to-green-50 border-0 shadow-lg hover:shadow-xl transition-all duration-300 group hover:scale-105">
//                 <CardContent className="p-8 text-center">
//                   <div className="mb-6 flex justify-center">
//                     <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-green-500 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
//                       <IconComponent className="w-8 h-8 text-white" />
//                     </div>
//                   </div>
                  
//                   <h3 className="text-xl font-bold text-gray-900 mb-4">
//                     {benefit.title}
//                   </h3>
                  
//                   <p className="text-gray-600 leading-relaxed">
//                     {benefit.description}
//                   </p>
//                 </CardContent>
//               </Card>
//             );
//           })}
//         </div>
//       </div>
//     </section>
//   );
// };

// export default SolutionsSection;